import hexRgb from 'hex-rgb';
import { cond, equals, always, T } from 'ramda';
import { useTheme as useTheme$1 } from '@team-griffin/react-dye';

var Curvature;

(function (Curvature) {
  Curvature[Curvature["OFF"] = 0] = "OFF";
  Curvature[Curvature["WEAK"] = 1] = "WEAK";
  Curvature[Curvature["STRONG"] = 2] = "STRONG";
})(Curvature || (Curvature = {}));

var TypographySizing;

(function (TypographySizing) {
  TypographySizing[TypographySizing["SMALL"] = 0] = "SMALL";
  TypographySizing[TypographySizing["MEDIUM"] = 1] = "MEDIUM";
  TypographySizing[TypographySizing["LARGE"] = 2] = "LARGE";
})(TypographySizing || (TypographySizing = {}));

var Spacing;

(function (Spacing) {
  Spacing[Spacing["SMALL"] = 0] = "SMALL";
  Spacing[Spacing["MEDIUM"] = 1] = "MEDIUM";
  Spacing[Spacing["LARGE"] = 2] = "LARGE";
})(Spacing || (Spacing = {}));

var GridUnit;

(function (GridUnit) {
  GridUnit[GridUnit["SMALL"] = 6] = "SMALL";
  GridUnit[GridUnit["MEDIUM"] = 8] = "MEDIUM";
  GridUnit[GridUnit["LARGE"] = 10] = "LARGE";
})(GridUnit || (GridUnit = {}));

var Colorway;

(function (Colorway) {
  Colorway["LIGHT"] = "LIGHT";
  Colorway["NEUTRAL"] = "NEUTRAL";
  Colorway["DARK"] = "DARK";
  Colorway["PRIMARY"] = "PRIMARY";
  Colorway["PRIMARY_GRADIENT"] = "PRIMARY_GRADIENT";
  Colorway["HIGHLIGHT"] = "HIGHLIGHT";
})(Colorway || (Colorway = {}));

var validateColor = function validateColor(color) {
  var test = /^#[0-9A-Fa-f]{6}|[0-9A-Fa-f]{3}/i;

  if (test.test(color)) {
    return color;
  }

  throw new Error("Not a valid color: ".concat(color));
};
var spacingToGridUnit = function spacingToGridUnit(spacing) {
  return cond([[equals(Spacing.SMALL), always(GridUnit.SMALL)], [equals(Spacing.MEDIUM), always(GridUnit.MEDIUM)], [equals(Spacing.LARGE), always(GridUnit.LARGE)], [T, always(GridUnit.MEDIUM)]])(spacing);
};
var gu2px = function gu2px(count, spacing) {
  var gu = spacingToGridUnit(spacing);
  return Math.floor(count * gu);
};
var hex2RgbString = function hex2RgbString(hex) {
  if (!hex) {
    return '';
  }

  var color = hexRgb(hex);
  return "".concat(color.red, ", ").concat(color.green, ", ").concat(color.blue);
};

function _typeof(obj) {
  "@babel/helpers - typeof";

  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof = function (obj) {
      return typeof obj;
    };
  } else {
    _typeof = function (obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof(obj);
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

var flattenObj = function flattenObj(obj, parent) {
  var res = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

  for (var key in obj) {
    if (obj.hasOwnProperty(key)) {
      var propName = parent ? "".concat(parent, "-").concat(key) : key;

      if (_typeof(obj[key]) === 'object') {
        flattenObj(obj[key], propName, res);
      } else {
        res[propName] = obj[key];
      }
    }
  }

  return res;
}; // take the theme object and return a set of
// css variables for the theme


var buildCssVars = function buildCssVars(theme) {
  if (_typeof(theme) !== 'object' || theme === null) {
    // eslint-disable-next-line no-console
    console.error('Invalid theme provided for CSS variables');
    return '';
  } // flatten in prep for mapping to css vars


  var flatTheme = flattenObj(theme); // add additional css variables for GU spacing
  // since we cannot do dynamic runtime stuff in css

  var guSizes = flattenObj({
    gu: {
      1: "".concat(gu2px(1, theme.spacing), "px"),
      2: "".concat(gu2px(2, theme.spacing), "px"),
      3: "".concat(gu2px(3, theme.spacing), "px"),
      4: "".concat(gu2px(4, theme.spacing), "px"),
      5: "".concat(gu2px(5, theme.spacing), "px"),
      6: "".concat(gu2px(6, theme.spacing), "px"),
      8: "".concat(gu2px(8, theme.spacing), "px"),
      10: "".concat(gu2px(10, theme.spacing), "px"),
      12: "".concat(gu2px(12, theme.spacing), "px"),
      16: "".concat(gu2px(16, theme.spacing), "px")
    }
  }); // build the RGB colors for palette, required for opacity functions
  // palette-primary-rgb: '255, 255, 255'

  var rgbPalette = Object.keys(flatTheme).filter(function (k) {
    return k.includes('palette');
  }).reduce(function (prev, cur) {
    return Object.assign(Object.assign({}, prev), _defineProperty({}, "".concat(cur, "-rgb"), hex2RgbString(flatTheme[cur])));
  }, {});
  var cssTheme = Object.assign({}, flatTheme, guSizes, rgbPalette); // build CSS variables from theme object, prepend 'theme'
  // --theme-palette-primary: #color
  // --theme-palette-illustrations-primary: #color

  var cssVarsString = Object.keys(cssTheme).reduce(function (prev, cur) {
    return "".concat(prev, "--theme-").concat(cur, ": ").concat(cssTheme[cur], ";");
  }, '');
  return ":root { ".concat(cssVarsString, " }");
}; // An object version of the theme that returns the css vars
// for simplicity and to save repetition and keep it as familiar as possible
// e.g.
// import { cssTheme } from '@ux/theme-utils';
// {
//   backgroundColor: cssTheme.palette.primary,
// }

var varPrefix = function varPrefix(property) {
  return "var(--theme-".concat(property, ")");
};

var cssTheme = {
  curvature: varPrefix('curvature'),
  greyTone: varPrefix('greyTone'),
  iconSet: varPrefix('iconSet'),
  spacing: varPrefix('spacing'),
  gu1: varPrefix('gu-1'),
  gu2: varPrefix('gu-2'),
  gu3: varPrefix('gu-3'),
  gu4: varPrefix('gu-4'),
  gu5: varPrefix('gu-5'),
  gu6: varPrefix('gu-6'),
  gu8: varPrefix('gu-8'),
  gu10: varPrefix('gu-10'),
  gu12: varPrefix('gu-12'),
  gu16: varPrefix('gu-16'),
  palette: {
    headerBackground: varPrefix('palette-headerBackground'),
    headerForeground: varPrefix('palette-headerForeground'),
    headerHighlight: varPrefix('palette-headerHighlight'),
    primary: varPrefix('palette-primary'),
    primaryContrast: varPrefix('palette-primaryContrast'),
    primaryGradient: varPrefix('palette-primaryGradient'),
    secondary: varPrefix('palette-secondary'),
    tertiary: varPrefix('palette-tertiary'),
    highlight: varPrefix('palette-highlight'),
    highlightContrast: varPrefix('palette-highlightContrast'),
    accent: varPrefix('palette-accent'),
    contrast: varPrefix('palette-contrast'),
    contrastAccent: varPrefix('palette-contrastAccent'),
    cta: varPrefix('palette-cta'),
    ctaContrast: varPrefix('palette-ctaContrast'),
    illustrations: {
      primary: varPrefix('palette-illustrations-primary'),
      primaryContrast: varPrefix('palette-illustrations-primaryContrast'),
      secondary: varPrefix('palette-illustrations-secondary'),
      secondaryContrast: varPrefix('palette-illustrations-secondaryContrast'),
      tertiary: varPrefix('palette-illustrations-tertiary'),
      tertiaryContrast: varPrefix('palette-illustrations-tertiaryContrast')
    },
    grey01: varPrefix('palette-grey01'),
    grey02: varPrefix('palette-grey02'),
    grey03: varPrefix('palette-grey03'),
    grey04: varPrefix('palette-grey04'),
    grey05: varPrefix('palette-grey05'),
    grey06: varPrefix('palette-grey06'),
    grey07: varPrefix('palette-grey07'),
    grey08: varPrefix('palette-grey08'),
    grey09: varPrefix('palette-grey09'),
    grey10: varPrefix('palette-grey10'),
    grey11: varPrefix('palette-grey11'),
    grey12: varPrefix('palette-grey12'),
    red01: varPrefix('palette-red01'),
    red02: varPrefix('palette-red02'),
    yellow01: varPrefix('palette-yellow01'),
    yellow02: varPrefix('palette-yellow02'),
    green01: varPrefix('palette-green01'),
    green02: varPrefix('palette-green02'),
    // RGB versions
    rgb: {
      headerBackground: varPrefix('palette-headerBackground-rgb'),
      headerForeground: varPrefix('palette-headerForeground-rgb'),
      headerHighlight: varPrefix('palette-headerHighlight-rgb'),
      primary: varPrefix('palette-primary-rgb'),
      primaryContrast: varPrefix('palette-primaryContrast-rgb'),
      primaryGradient: varPrefix('palette-primaryGradient-rgb'),
      secondary: varPrefix('palette-secondary-rgb'),
      tertiary: varPrefix('palette-tertiary-rgb'),
      highlight: varPrefix('palette-highlight-rgb'),
      highlightContrast: varPrefix('palette-highlightContrast-rgb'),
      accent: varPrefix('palette-accent-rgb'),
      contrast: varPrefix('palette-contrast-rgb'),
      contrastAccent: varPrefix('palette-contrastAccent-rgb'),
      cta: varPrefix('palette-cta-rgb'),
      ctaContrast: varPrefix('palette-ctaContrast-rgb'),
      illustrations: {
        primary: varPrefix('palette-illustrations-primary-rgb'),
        primaryContrast: varPrefix('palette-illustrations-primaryContrast-rgb'),
        secondary: varPrefix('palette-illustrations-secondary-rgb'),
        secondaryContrast: varPrefix('palette-illustrations-secondaryContrast-rgb'),
        tertiary: varPrefix('palette-illustrations-tertiary-rgb'),
        tertiaryContrast: varPrefix('palette-illustrations-tertiaryContrast-rgb')
      },
      grey01: varPrefix('palette-grey01-rgb'),
      grey02: varPrefix('palette-grey02-rgb'),
      grey03: varPrefix('palette-grey03-rgb'),
      grey04: varPrefix('palette-grey04-rgb'),
      grey05: varPrefix('palette-grey05-rgb'),
      grey06: varPrefix('palette-grey06-rgb'),
      grey07: varPrefix('palette-grey07-rgb'),
      grey08: varPrefix('palette-grey08-rgb'),
      grey09: varPrefix('palette-grey09-rgb'),
      grey10: varPrefix('palette-grey10-rgb'),
      grey11: varPrefix('palette-grey11-rgb'),
      grey12: varPrefix('palette-grey12-rgb'),
      red01: varPrefix('palette-red01-rgb'),
      red02: varPrefix('palette-red02-rgb'),
      yellow01: varPrefix('palette-yellow01-rgb'),
      yellow02: varPrefix('palette-yellow02-rgb'),
      green01: varPrefix('palette-green01-rgb'),
      green02: varPrefix('palette-green02-rgb')
    }
  },
  typography: {
    display: varPrefix('typography-display'),
    content: varPrefix('typography-content'),
    displayURL: varPrefix('typography-displayURL'),
    contentURL: varPrefix('typography-URL'),
    sizing: varPrefix('typography-sizing'),
    gwfUrl: varPrefix('typography-gwfUrl')
  }
};

var useTheme = function useTheme() {
  return {
    theme: useTheme$1()
  };
};

export { Colorway, Curvature, GridUnit, Spacing, TypographySizing, buildCssVars, cssTheme, gu2px, hex2RgbString, spacingToGridUnit, useTheme, validateColor };
